import React, { useState, useEffect } from "react";
import SaveButtonWithModal from "./SaveButtonWithModel";
import { uploadToCloudinary } from "../utils/CloudinaryUpload";

const LOCAL_STORAGE_KEY = "organizationFormData";

const OrganizationForm = ({
  formData,
  onFormDataChange,
  selectedOption,
  onCertificatesUrlChange,
  onIdUrlChange,
  onOptionChange,
  onSave,
  onProfileImageChange
}) => {
  // --- State initialization
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCircles, setSelectedCircles] = useState({});
  const [images, setImages] = useState(Array(1).fill(null)); // 4 boxes
  const [certFiles, setCertFiles] = useState(Array(1).fill(null));
  const [showModal, setShowModal] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("All Documents");
  const [boxPlaceholders, setBoxPlaceholders] = useState([
    "National identity card",
    "Business registration",
    "Trade Certificate",
    "Tax compliance",
  ]);
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
  const [profileImage, setProfileImage] = useState(null);
  // --- Restore state from localStorage on mount
  useEffect(() => {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      setImages(parsed.images || Array(1).fill(null));
      setBoxPlaceholders(parsed.boxPlaceholders || [
        "National identity card",
        "Business registration",
        "Trade Certificate",
        "Tax compliance",
      ]);
      setCurrentBoxIndex(parsed.currentBoxIndex || 0);
      setSelectedDocumentType(parsed.selectedDocumentType || "All Documents");
      // If you want to restore more state, do it here
    }
  }, []);

  // --- Save image as base64 for localStorage
  const fileToDataUrl = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });

  // --- Image handlers
  const handleDeleteImage = (index) => {
    setImages((prevImages) => {
      const newImages = [...prevImages];
      newImages[index] = null;
      return newImages;
    });
    setCertFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles[index] = null;
      return newFiles;
    });
    setBoxPlaceholders(prev => {
      const newPlaceholders = [...prev];
      newPlaceholders[index] = [
        "National identity card",
        "Business registration",
        "Trade Certificate",
        "Tax compliance"
      ][index];
      return newPlaceholders;
    });
    if (index === currentBoxIndex) {
      setCurrentBoxIndex(index);
    }
  };

// In Profile component
// Update the handleImageChange function to properly pass the image data
// Update the handleImageChange to properly structure the image data
const handleImageChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const imageData = {
    previewUrl: URL.createObjectURL(file),
    file, // The actual File object
    name: file.name,
    type: file.type,
    size: file.size
  };

  setProfileImage(imageData);
  
  // Notify parent component with the structured data
  if (onProfileImageChange) {
    onProfileImageChange(imageData);
  }
};
  const handleIdChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdFile(file);
      setIdImageUrl(URL.createObjectURL(file));
    }
  };

  const handleDocumentTypeSelect = (type) => {
    setSelectedDocumentType(type);
    setDocumentDropdownOpen(false);
    setBoxPlaceholders(prev => {
      const newPlaceholders = [...prev];
      newPlaceholders[currentBoxIndex] = type;
      return newPlaceholders;
    });
    setCurrentBoxIndex((prevIndex) => (prevIndex + 1) % 4);
  };

  // --- Save handler with localStorage persistence
  const handleSave = async () => {
    const hasCertificates = certFiles.some((file) => file !== null);
    if (!hasCertificates) {
      setError("At least one certificate is required.");
      setShowModal(false);
      return;
    }
    setError("");
    setSaving(true);
    let certUrls = [];
    let idUrl = "";

    try {
      // Cloudinary test upload (optional)
      const testFile = new File(["test"], "test.txt", { type: "text/plain" });
      await uploadToCloudinary(testFile);
    } catch (testError) {
      setError("Failed to connect to Cloudinary. Please check your configuration.");
      setSaving(false);
      return;
    }

    for (let i = 0; i < certFiles.length; i++) {
      if (certFiles[i]) {
        try {
          const url = await uploadToCloudinary(certFiles[i]);
          certUrls.push({
            url,
            type: boxPlaceholders[i]
          });
        } catch (error) {
          setError(`Failed to upload certificate ${i + 1}. Please try again.`);
          setSaving(false);
          return;
        }
      }
    }

    if (onCertificatesUrlChange) onCertificatesUrlChange(certUrls);

    if (idFile) {
      try {
        idUrl = await uploadToCloudinary(idFile);
        if (onIdUrlChange) onIdUrlChange(idUrl);
      } catch (error) {
        setError("Failed to upload ID document. Please try again.");
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    setShowModal(true);

    // Persist to localStorage (images as base64, other selections)
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        images,
        boxPlaceholders,
        currentBoxIndex,
        selectedDocumentType,
        // Add more fields if needed (e.g., idImageUrl, etc.)
      })
    );

    onSave({
      ...formData,
      certificates: certUrls,
      idDocument: idUrl,
      typeOfOrganization: selectedOption,
    });
  };

  useEffect(() => {
    return () => {
      images.forEach((imgSrc) => {
        // No need to revoke base64 URLs
      });
      if (idImageUrl) URL.revokeObjectURL(idImageUrl);
    };
  }, [images, idImageUrl]);

  return (
    <div className="md:px-0 md:mt-6">
      <div className="">
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <div 
              className="relative cursor-pointer"
              onClick={() => setDocumentDropdownOpen(!documentDropdownOpen)}
            >
              <div className="flex items-center gap-1">
                <span className="text-[8px] md:text-[36px] font-bold">
                  Profile
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <p className="font-normal italic text-[6px] md:text-[24px]">
              Please insert pictures / images for profile
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:gap-2 gap-1 ">
          {images.slice(0, 1).map((imgSrc, index) => (
            <div key={index} className="flex flex-col  md:-mt-2">
              <div className="relative bg-[#E7EBEB] rounded-[5px] h-[140px] md:h-[650px] w-full overflow-hidden">
              {profileImage ? (
          <img
            src={profileImage.previewUrl}
            alt="Profile"
            className="object-cover w-full h-full"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
          </div>
        )}
                <label className="absolute top-1 right-1 md:top-3 md:right-3 cursor-pointer">
                  <img
                    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745668623/Add_square_duotone_gz7vxe.png"
                    alt="Add Icon"
                    className="  w-4 md:w-[40px] h-4 md:h-[40px]"
                  />
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleImageChange(e, index)}
                    className="hidden"
                  />
                </label>
                  <div className="absolute md:bottom-2 md:right-2 bottom-1 right-1">
                    <img
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1746296659/Trash_ya7kiu.png"
                      alt="Trash Icon"
                      className=" w-3 md:w-[24px] h-3 md:h-[24px] cursor-pointer"
                      onClick={() => handleDeleteImage(index)}
                    />
                  </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {error && (
        <div className="mt-2 text-red-600 text-xs text-center">{error}</div>
      )}
      <SaveButtonWithModal onClick={handleSave} loading={saving} />
    </div>
  );
};

export default OrganizationForm;